/**
 * Set from the `INTERNAL - UTM Parameters` tag in Google Tag Manager.
 */
declare global {
  interface Window {
    MX_UTM_PARAMS?: string;
  }
}

/**
 * Returns utm parameters as a query string to be appended to a URL.
 */
export function getUtmParameters(params: { procedureUid: string }): string {
  const windowParameters = getWindowParameters();

  const utmParams = [
    windowParameters,
    ...encodeQueryParams({
      gpl_procedure_uid: params.procedureUid,
    }),
  ]
    .filter(Boolean)
    .join("&");

  if (utmParams) {
    return `?${utmParams}`;
  }
  return "";
}

/**
 * Taken from the `INTERNAL - UTM Parameters` tag in Google Tag Manager.
 * https://tagmanager.google.com/#/container/accounts/4702447132/containers/12493591/workspaces/134/tags/184
 */
type UtmParameters = {
  "~ad_set_name"?: string;
  "~ad_set_id"?: string;
  "~campaign"?: string;
  "~channel"?: string;
  "~feature"?: string;
  "~keyword"?: string;
  "~last_page_seen"?: string;
  "~referrer"?: string;
  "~visitor_id"?: string;
  gclid?: string;
};

/**
 * Get the utm parameters from the window object and transform them into an object.
 */
export function getExistingUtmParameters(): UtmParameters {
  const windowParameters = getWindowParameters();
  if (!windowParameters) {
    return {};
  }

  const parameters: Record<string, string> = {};

  windowParameters.split("&").forEach((parameter) => {
    const [key, value] = parameter.split("=");

    if (typeof key === "string" && typeof value === "string") {
      parameters[key] = value;
    }
  });

  return parameters;
}

/**
 * Retrieves the utm parameters from the window object.
 */
function getWindowParameters(): string {
  if (typeof window === "undefined") {
    return "";
  }

  return window.MX_UTM_PARAMS ?? "";
}

/**
 * Transforms the parameters object into an array of encoded query parameters.
 */
function encodeQueryParams(params: Record<string, any>): string[] {
  return Object.entries(params).map(([key, value]) =>
    typeof value !== "undefined" ? `${encodeURIComponent(key)}=${encodeURIComponent(value)}` : "",
  );
}
